/**
 * Shared pagination control.
 *
 * One component so every paged table in the product looks and behaves the same:
 *   « first  ‹ prev   1 … 4 [5] 6 … 20   next ›  » last     + rows-per-page
 *
 * It is presentation-only and source-agnostic. The crypto tables drive it from
 * server-side limit/offset; the asset grid drives it from TanStack's
 * client-side pagination model. Both pass the same props.
 *
 * pageIndex is 0-based (page 1 shown to the user is pageIndex 0).
 */

export interface PaginationProps {
  /** 0-based index of the current page. */
  pageIndex: number;
  /** Total number of pages (>= 1). */
  pageCount: number;
  /** Called with the target 0-based page index. */
  onPageChange: (pageIndex: number) => void;
  /** Current rows-per-page. */
  pageSize: number;
  /** Called with the new rows-per-page. */
  onPageSizeChange: (pageSize: number) => void;
  /** Total item count across all pages (for the "X–Y of Z" label). */
  totalItems: number;
  /** Plural noun for the label, e.g. "keys", "assets". */
  itemLabel?: string;
  /** Rows-per-page choices. */
  pageSizeOptions?: number[];
  /** Disables all controls (e.g. while a page is loading). */
  disabled?: boolean;
  className?: string;
}

const DEFAULT_PAGE_SIZES = [25, 50, 100, 200];

/**
 * Compact page list with ellipses: always the first and last page, the current
 * page, and one sibling either side; gaps become a single 'ellipsis' marker.
 * e.g. current=5 of 20 → [0, 'ellipsis', 4, 5, 6, 'ellipsis', 19]
 */
export function buildPageList(current: number, count: number): (number | 'ellipsis')[] {
  if (count <= 7) return Array.from({ length: count }, (_, i) => i);
  const wanted = new Set<number>([0, count - 1, current, current - 1, current + 1]);
  const sorted = [...wanted].filter((p) => p >= 0 && p < count).sort((a, b) => a - b);
  const out: (number | 'ellipsis')[] = [];
  let prev = -1;
  for (const p of sorted) {
    if (prev !== -1 && p - prev > 1) out.push('ellipsis');
    out.push(p);
    prev = p;
  }
  return out;
}

export function Pagination({
  pageIndex,
  pageCount,
  onPageChange,
  pageSize,
  onPageSizeChange,
  totalItems,
  itemLabel = 'items',
  pageSizeOptions = DEFAULT_PAGE_SIZES,
  disabled = false,
  className = '',
}: PaginationProps) {
  const safeCount = Math.max(1, pageCount);
  const from = totalItems === 0 ? 0 : pageIndex * pageSize + 1;
  const to = Math.min(totalItems, (pageIndex + 1) * pageSize);
  const atStart = pageIndex <= 0;
  const atEnd = pageIndex >= safeCount - 1;

  // Ensure the current page size is offered even if it isn't in the defaults.
  const sizeOptions = pageSizeOptions.includes(pageSize)
    ? pageSizeOptions
    : [...pageSizeOptions, pageSize].sort((a, b) => a - b);

  const navBtn =
    'min-w-[28px] h-7 px-2 text-xs border border-ink-200 rounded bg-white ' +
    'hover:bg-ink-50 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white';

  return (
    <div className={`flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-ink-500 ${className}`}>
      <label className="flex items-center gap-1.5">
        Rows:
        <select
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          disabled={disabled}
          className="border border-ink-200 rounded px-1.5 py-1 bg-white text-ink"
          aria-label="Rows per page"
        >
          {sizeOptions.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </label>

      <span className="tabular-nums">
        {totalItems === 0
          ? `0 ${itemLabel}`
          : `${from.toLocaleString()}–${to.toLocaleString()} of ${totalItems.toLocaleString()} ${itemLabel}`}
      </span>

      <nav className="flex items-center gap-1" aria-label="Pagination">
        <button
          type="button"
          className={navBtn}
          disabled={disabled || atStart}
          onClick={() => onPageChange(0)}
          aria-label="First page"
          title="First page"
        >
          «
        </button>
        <button
          type="button"
          className={navBtn}
          disabled={disabled || atStart}
          onClick={() => onPageChange(pageIndex - 1)}
          aria-label="Previous page"
          title="Previous page"
        >
          ‹
        </button>

        {buildPageList(pageIndex, safeCount).map((p, i) =>
          p === 'ellipsis' ? (
            <span key={`e${i}`} className="px-1 select-none text-ink-300">…</span>
          ) : (
            <button
              key={p}
              type="button"
              disabled={disabled}
              onClick={() => onPageChange(p)}
              aria-label={`Page ${p + 1}`}
              aria-current={p === pageIndex ? 'page' : undefined}
              className={
                p === pageIndex
                  ? 'min-w-[28px] h-7 px-2 text-xs border border-accent rounded bg-accent text-white font-medium'
                  : navBtn
              }
            >
              {(p + 1).toLocaleString()}
            </button>
          ),
        )}

        <button
          type="button"
          className={navBtn}
          disabled={disabled || atEnd}
          onClick={() => onPageChange(pageIndex + 1)}
          aria-label="Next page"
          title="Next page"
        >
          ›
        </button>
        <button
          type="button"
          className={navBtn}
          disabled={disabled || atEnd}
          onClick={() => onPageChange(safeCount - 1)}
          aria-label="Last page"
          title="Last page"
        >
          »
        </button>
      </nav>
    </div>
  );
}
